// =============================================================
// Ctrl+K command palette (Phase 9.3 global chrome).
//
// Global search across assets / work orders / spares / people via GET /search
// (RBAC-aware server-side). Keyboard-driven: type to search (debounced),
// ArrowUp/Down to move, Enter to navigate, Esc to close. Results are grouped by
// kind; selecting a hit navigates to its SPA path and closes the palette.
//
// Opened globally with Ctrl/Cmd+K (registered in ChromeHost). Follows the repo's
// established scrim+panel overlay pattern (not native <dialog>) so it matches the
// binding design system; adds proper focus management + ARIA listbox semantics.
// =============================================================

import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SearchHit } from "@perpetuum/core";
import { useSearch } from "../lib/queries.js";
import { useScopedT } from "../lib/i18n-keys.js";
import { Icon, ICONS } from "./primitives.js";
import { EmptyState } from "./EmptyState.js";
import { Skeleton } from "./Skeleton.js";

const KIND_ICON: Record<string, string> = {
  device: ICONS.wrench,
  task: ICONS.list,
  spare: ICONS.box,
  person: ICONS.users,
};

export function CommandPalette({ onClose }: { onClose: () => void }) {
  const t = useScopedT();
  const [q, setQ] = useState("");
  const [debounced, setDebounced] = useState("");
  const [active, setActive] = useState(0);
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);

  // Debounce the query (200ms) so we don't fire a request per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setDebounced(q), 200);
    return () => clearTimeout(id);
  }, [q]);

  const { data: groups, isFetching } = useSearch(debounced);

  // Flatten groups → a single ordered hit list for arrow navigation.
  const flat = useMemo<SearchHit[]>(() => (groups ?? []).flatMap((g) => g.items), [groups]);

  // Keep the active index in range as results change.
  useEffect(() => {
    setActive(0);
  }, [flat.length]);

  // Autofocus the input on open.
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const go = (hit: SearchHit) => {
    navigate(hit.path);
    onClose();
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      onClose();
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
      return;
    }
    if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
      return;
    }
    if (e.key === "Enter") {
      e.preventDefault();
      const hit = flat[active];
      if (hit) go(hit);
    }
  };

  // Running counter so the per-group items map onto the flat active index.
  let counter = -1;

  return (
    <div className="cmdk-scrim" onClick={onClose} data-testid="cmdk">
      <div
        className="cmdk-panel"
        role="dialog"
        aria-modal="true"
        aria-label={t("cmdk.label")}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={onKeyDown}
      >
        <div className="cmdk-input-row">
          <Icon d={ICONS.search} size={18} />
          <input
            ref={inputRef}
            className="cmdk-input"
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={t("cmdk.placeholder")}
            aria-label={t("cmdk.searchLabel")}
            aria-controls="cmdk-results"
            data-testid="cmdk-input"
          />
          <kbd className="cmdk-esc">{t("cmdk.esc")}</kbd>
        </div>

        <div className="cmdk-results" id="cmdk-results" role="listbox" aria-label={t("cmdk.resultsLabel")}>
          {debounced.trim().length === 0 && (
            <div className="cmdk-hint">{t("cmdk.hint")}</div>
          )}
          {debounced.trim().length > 0 && isFetching && (groups?.length ?? 0) === 0 && (
            <div style={{ padding: "8px 12px" }} data-testid="cmdk-loading">
              <Skeleton variant="text" count={4} />
            </div>
          )}
          {debounced.trim().length > 0 && (groups?.length ?? 0) === 0 && !isFetching && (
            <div data-testid="cmdk-empty">
              <EmptyState compact icon={ICONS.search} title={t("cmdk.noMatches", { q: debounced })} />
            </div>
          )}
          {(groups ?? []).map((group) => (
            <div className="cmdk-group" key={group.kind}>
              <div className="cmdk-group-label">{group.label}</div>
              {group.items.map((hit) => {
                counter += 1;
                const idx = counter;
                return (
                  <button
                    key={hit.id}
                    type="button"
                    role="option"
                    aria-selected={idx === active}
                    className={`cmdk-item ${idx === active ? "active" : ""}`}
                    onMouseEnter={() => setActive(idx)}
                    onClick={() => go(hit)}
                    data-testid={`cmdk-item-${hit.kind}-${hit.id}`}
                  >
                    <Icon d={KIND_ICON[hit.kind] ?? ICONS.box} size={15} />
                    <span className="cmdk-item-title">{hit.title}</span>
                    {hit.subtitle && <span className="cmdk-item-sub">{hit.subtitle}</span>}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
